import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { GitBranch, ArrowsClockwise, MagnifyingGlass, X, Copy, PencilSimple, Trash, Paperclip, FileText, FileImage, FilePdf, FileDoc, FileXls, FilePpt, FileZip, FileCsv } from '@phosphor-icons/react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useRealtime } from '../context/RealtimeContext';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from './ConfirmDialog';
import { useT } from '../i18n/I18nContext';
import { labelColor } from '../lib/labels';
import { AGENT_AUTHOR_ID } from '../lib/agent';
import AgentPanel from './AgentPanel';

// Markdown images for pasted screenshots are stored as relative paths
// (``/api/uploads/...``). Browsers resolve those against the page origin (the
// frontend dev server), so without rewriting they 404. Prepend the backend URL
// at render time — the stored markdown stays portable across environments.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function iconForMime(mime) {
  if (!mime) return FileText;
  if (mime.startsWith('image/')) return FileImage;
  if (mime === 'application/pdf') return FilePdf;
  if (mime.includes('word')) return FileDoc;
  if (mime.includes('sheet') || mime === 'application/vnd.ms-excel') return FileXls;
  if (mime.includes('presentation') || mime === 'application/vnd.ms-powerpoint') return FilePpt;
  if (mime === 'application/zip') return FileZip;
  if (mime === 'text/csv') return FileCsv;
  return FileText;
}

function formatBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

const ATTACH_ACCEPT = [
  'image/png', 'image/jpeg', 'image/gif', 'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain', 'text/csv', 'text/markdown',
  'application/zip', 'application/json',
].join(',');

function absolutizeMarkdownSrc(src) {
  if (!src) return src;
  if (src.startsWith('/api/')) return `${BACKEND_URL}${src}`;
  return src;
}


function serializeToMarkdown(el) {
  let text = '';
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent;
    } else if (node.nodeName === 'IMG') {
      const dataSrc = node.getAttribute('data-src');
      if (dataSrc) text += `![](${dataSrc})`;
    } else if (node.nodeName === 'BR') {
      text += '\n';
    } else if (node.nodeName === 'DIV' || node.nodeName === 'P') {
      if (text && !text.endsWith('\n')) text += '\n';
      text += serializeToMarkdown(node);
    } else {
      text += serializeToMarkdown(node);
    }
  }
  return text;
}

function populateDescContentEditable(el, md) {
  el.innerHTML = '';
  const parts = (md || '').split(/(!\[[^\]]*\]\([^)]*\))/g);
  for (const part of parts) {
    const imgMatch = part.match(/^!\[[^\]]*\]\(([^)]*)\)$/);
    if (imgMatch) {
      const url = imgMatch[1];
      const img = document.createElement('img');
      img.src = absolutizeMarkdownSrc(url);
      img.setAttribute('data-src', url);
      img.style.cssText = 'max-height:200px;max-width:100%;border-radius:4px;display:block;margin:4px 0';
      el.appendChild(img);
    } else if (part) {
      const lines = part.split('\n');
      lines.forEach((line, i) => {
        if (line) el.appendChild(document.createTextNode(line));
        if (i < lines.length - 1) el.appendChild(document.createElement('br'));
      });
    }
  }
}

// execCommand fallback keeps copy working on http:// origins, where
// navigator.clipboard is undefined.
async function writeClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
}

// Copies text that is still being fetched. Safari only allows a clipboard write
// inside the click handler, so hand it a pending ClipboardItem rather than
// awaiting the fetch first; other browsers fall back to writing once it resolves.
async function writeClipboardAsync(textPromise) {
  if (window.ClipboardItem && navigator.clipboard?.write) {
    try {
      const blob = textPromise.then((text) => new Blob([text], { type: 'text/plain' }));
      await navigator.clipboard.write([new window.ClipboardItem({ 'text/plain': blob })]);
      return;
    } catch (err) {
      // A failed fetch must surface as-is rather than retrying via the fallback.
      await textPromise;
    }
  }
  await writeClipboard(await textPromise);
}

function isStaleTicketRequestError(err) {
  return (
    err?.code === 'ERR_CANCELED' ||
    err?.name === 'CanceledError' ||
    err?.response?.status === 404
  );
}

export default function TicketDetail({ ticket, users, projectMembers, project, onClose, onChange, onDeleteTicket }) {
  const t = useT();
  const { user: currentUser } = useAuth();
  // ``users`` is the global list — needed so the comment author lookup still
  // resolves names for people who were removed from the project. The assignee
  // picker uses ``projectMembers`` so you can't pick someone outside the team.
  const assignableUsers = projectMembers ?? users ?? [];
  const [editing, setEditing] = useState(ticket);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentBody, setEditingCommentBody] = useState('');
  const [commentDeleteTarget, setCommentDeleteTarget] = useState(null);
  const [commentDeleteLoading, setCommentDeleteLoading] = useState(false);
  const [descEditMode, setDescEditMode] = useState(false);
  const [labelInput, setLabelInput] = useState('');
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [creatingBranchRepoId, setCreatingBranchRepoId] = useState(null);
  const [agentSteps, setAgentSteps] = useState([]);
  const [commentImages, setCommentImages] = useState([]); // uploaded, not yet posted: [{url}]
  const [uploadingCommentImage, setUploadingCommentImage] = useState(false);
  const commentFileRef = useRef(null);
  const commentInputRef = useRef(null);

  // Grow the comment box with its content (capped by max-h), and shrink it
  // back once the comment is sent.
  useEffect(() => {
    const el = commentInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [newComment]);
  const descEditRef = useRef(null);
  const fileInputRef = useRef(null);

  const enterDescEdit = () => {
    setDescEditMode(true);
    requestAnimationFrame(() => {
      if (descEditRef.current) {
        populateDescContentEditable(descEditRef.current, editing.description || '');
        descEditRef.current.focus();
      }
    });
  };

  const exitDescEdit = () => {
    const md = descEditRef.current ? serializeToMarkdown(descEditRef.current) : '';
    setEditing((prev) => ({ ...prev, description: md }));
    setDescEditMode(false);
    save({ description: md });
  };
  const { subscribe } = useRealtime();

  useEffect(() => {
    if (!ticket?.id) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (lightboxSrc) { setLightboxSrc(null); return; }
      if (deleteConfirmOpen) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ticket?.id, onClose, deleteConfirmOpen, lightboxSrc]);

  useEffect(() => {
    setEditing(ticket);
    setDescEditMode(false);
    setAgentSteps([]);
    if (!ticket?.id) return undefined;

    const ac = new AbortController();
    const cfg = { signal: ac.signal };

    api
      .get(`/tickets/${ticket.id}/comments`, cfg)
      .then((r) => setComments(r.data))
      .catch((err) => {
        if (!isStaleTicketRequestError(err)) console.warn(err);
      });

    return () => ac.abort();
    // Deliberately keyed on the id only: re-syncing on every new ``ticket``
    // object identity (realtime updates re-create it) would clobber in-flight
    // edits and refetch the comments on each keystroke-triggered save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id]);

  // Subscribe to realtime events for this ticket
  useEffect(() => {
    if (!editing?.id) return;
    const unsub = subscribe(editing.id, (msg) => {
      if (msg.type === 'ticket_updated') {
        setEditing((prev) => ({ ...prev, ...msg.payload }));
        onChange && onChange();
      } else if (msg.type === 'comment_added') {
        // Our own comments arrive here too, after the POST already added them.
        setComments((prev) => (prev.some((c) => c.id === msg.payload.id) ? prev : [...prev, msg.payload]));
      } else if (msg.type === 'agent_step') {
        setAgentSteps((prev) => [...prev.slice(-199), msg.payload]);
      }
    });
    return unsub;
  }, [editing?.id, subscribe, onChange]);

  const save = async (overrides) => {
    const next = overrides ? { ...editing, ...overrides } : editing;
    try {
      const { data } = await api.patch(`/tickets/${ticket.id}`, {
        title: next.title,
        description: next.description,
        priority: next.priority,
        assignee_id: next.assignee_id || null,
        status: next.status,
        labels: next.labels,
        target_repo_ids: next.target_repo_ids ?? [],
      });
      setEditing(data);
      onChange();
      toast.success(t('common.saved'));
    } catch (err) {
      toast.error(t('common.saveFailed'));
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() && commentImages.length === 0) return;
    // Images travel as markdown in the body — the agent's ticket context turns
    // them into [IMAGE n] markers it downloads and reads.
    const body = [newComment.trim(), ...commentImages.map((img) => `![](${img.url})`)]
      .filter(Boolean).join('\n\n');
    const { data } = await api.post(`/tickets/${ticket.id}/comments`, { body });
    setComments((prev) => (prev.some((c) => c.id === data.id) ? prev : [...prev, data]));
    setNewComment('');
    setCommentImages([]);
  };

  const uploadCommentImage = async (file) => {
    if (!file || !file.type.startsWith('image/') || !ticket?.id) return;
    setUploadingCommentImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name || 'pasted.png');
      const { data } = await api.post(`/tickets/${ticket.id}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCommentImages((prev) => [...prev, { url: data.url }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('ticketDetail.uploadFailedToast'));
    } finally {
      setUploadingCommentImage(false);
    }
  };

  const handleCommentPaste = (e) => {
    const file = Array.from(e.clipboardData?.files || []).find((f) => f.type.startsWith('image/'));
    if (!file) return; // plain text paste goes through as usual
    e.preventDefault();
    uploadCommentImage(file);
  };

  const startEditComment = (c) => {
    setEditingCommentId(c.id);
    setEditingCommentBody(c.body);
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentBody('');
  };

  const saveEditComment = async () => {
    const body = editingCommentBody.trim();
    if (!body || !editingCommentId) return;
    try {
      const { data } = await api.patch(
        `/tickets/${ticket.id}/comments/${editingCommentId}`,
        { body },
      );
      setComments((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      cancelEditComment();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.saveFailed'));
    }
  };

  const confirmDeleteComment = async () => {
    if (!commentDeleteTarget) return;
    setCommentDeleteLoading(true);
    try {
      await api.delete(`/tickets/${ticket.id}/comments/${commentDeleteTarget}`);
      setComments((prev) => prev.filter((c) => c.id !== commentDeleteTarget));
      setCommentDeleteTarget(null);
      if (editingCommentId === commentDeleteTarget) cancelEditComment();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('ticketDetail.commentDeleteFailed'));
      throw err;
    } finally {
      setCommentDeleteLoading(false);
    }
  };

  const addLabel = (raw) => {
    const cleaned = (raw || '').trim().replace(/^,+|,+$/g, '').trim();
    if (!cleaned) {
      setLabelInput('');
      return;
    }
    const current = editing.labels || [];
    if (current.includes(cleaned)) {
      setLabelInput('');
      return;
    }
    const next = [...current, cleaned];
    setEditing((prev) => ({ ...prev, labels: next }));
    setLabelInput('');
    save({ labels: next });
  };

  const removeLabel = (label) => {
    const next = (editing.labels || []).filter((l) => l !== label);
    setEditing((prev) => ({ ...prev, labels: next }));
    save({ labels: next });
  };

  const onLabelKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addLabel(labelInput);
    } else if (e.key === 'Backspace' && labelInput === '' && (editing.labels || []).length > 0) {
      e.preventDefault();
      removeLabel(editing.labels[editing.labels.length - 1]);
    }
  };

  const handleAttachClick = () => {
    if (!ticket?.id || uploadingAttachment) return;
    fileInputRef.current?.click();
  };

  const handleAttachChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !ticket?.id) return;
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const { data } = await api.post(`/tickets/${ticket.id}/files`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditing(data);
      onChange && onChange();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 415) toast.error(detail || t('newTicket.attachUnsupported'));
      else if (status === 413) toast.error(detail || t('newTicket.attachTooLarge'));
      else toast.error(detail || t('ticketDetail.uploadFailedToast'));
    } finally {
      setUploadingAttachment(false);
    }
  };

  const removeAttachment = async (filename) => {
    if (!ticket?.id) return;
    try {
      const { data } = await api.delete(
        `/tickets/${ticket.id}/files/${encodeURIComponent(filename)}`,
      );
      setEditing(data);
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.saveFailed'));
    }
  };

  const handleDescriptionPaste = async (e) => {
    if (!ticket?.id) return;
    const cd = e.clipboardData;
    if (!cd) return;

    // Prevent the browser from inserting clipboard HTML/images itself —
    // we control exactly what goes into the contenteditable.
    e.preventDefault();

    const sel = window.getSelection();
    const range = sel?.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;

    let imageFile = Array.from(cd.files || []).find((f) => f.type.startsWith('image/'));
    if (!imageFile) {
      const fileItem = Array.from(cd.items || []).find(
        (it) => it.kind === 'file' && it.type.startsWith('image/'),
      );
      if (fileItem) imageFile = fileItem.getAsFile();
    }

    let htmlDataUrl = null;
    if (!imageFile) {
      const html = cd.getData('text/html');
      const m = html && html.match(/<img[^>]+src="(data:image\/[a-zA-Z+]+;base64,[^"]+)"/);
      if (m) htmlDataUrl = m[1];
    }

    if (!imageFile && !htmlDataUrl) {
      // Plain text paste — insert manually so the cursor position is preserved.
      const text = cd.getData('text/plain');
      if (text) document.execCommand('insertText', false, text);
      return;
    }

    const loadingEl = document.createElement('span');
    loadingEl.textContent = 'Загружается...';
    loadingEl.style.color = '#94a3b8';
    loadingEl.style.fontStyle = 'italic';
    if (range) {
      range.deleteContents();
      range.insertNode(loadingEl);
      const after = document.createRange();
      after.setStartAfter(loadingEl);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
    } else {
      descEditRef.current?.appendChild(loadingEl);
    }

    if (!imageFile && htmlDataUrl) {
      const res = await fetch(htmlDataUrl);
      const blob = await res.blob();
      imageFile = new File([blob], 'pasted.png', { type: blob.type });
    }

    try {
      const fd = new FormData();
      fd.append('file', imageFile, imageFile.name || 'pasted.png');
      const { data } = await api.post(`/tickets/${ticket.id}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const img = document.createElement('img');
      img.src = absolutizeMarkdownSrc(data.url);
      img.setAttribute('data-src', data.url);
      img.style.cssText = 'max-height:200px;max-width:100%;border-radius:4px;display:block;margin:4px 0';
      loadingEl.replaceWith(img);
      const newSel = window.getSelection();
      const newRange = document.createRange();
      newRange.setStartAfter(img);
      newRange.collapse(true);
      newSel.removeAllRanges();
      newSel.addRange(newRange);
    } catch (err) {
      loadingEl.remove();
      toast.error(err?.response?.data?.detail || t('ticketDetail.uploadFailedToast'));
    }
  };

  const copyCheckoutCmd = async (branch) => {
    if (!branch) return;
    const cmd = `git checkout ${branch}`;
    try {
      await writeClipboard(cmd);
      toast.success(t('ticketDetail.checkoutCopied', { cmd }));
    } catch (err) {
      toast.error(t('ticketDetail.copyFailed'));
    }
  };

  // Copies the whole ticket as text an AI coding agent can act on: screenshots
  // stay where they sit in the prose as [IMAGE n] markers, with one curl
  // command up top to fetch them. The server renders it (agent-context) so the
  // integration API hands agents exactly the same text.
  const copyContent = async () => {
    if (!editing?.id) return;
    const ctx = api.get(`/tickets/${editing.id}/agent-context`).then((r) => r.data);
    try {
      await writeClipboardAsync(ctx.then((data) => data.text));
      const { images } = await ctx;
      toast.success(
        images.length
          ? t('ticketDetail.contentCopiedWithImages', { count: images.length })
          : t('ticketDetail.contentCopied'),
      );
    } catch (err) {
      toast.error(t('ticketDetail.copyFailed'));
    }
  };

  const createBranch = async (repoId) => {
    if (creatingBranchRepoId) return;
    setCreatingBranchRepoId(repoId);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/branches`, { repo_id: repoId });
      setEditing(data);
      onChange();
      toast.success(t('ticketDetail.branchCreatedToast'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('ticketDetail.branchCreateFailed'));
    } finally {
      setCreatingBranchRepoId(null);
    }
  };

  const runReview = async () => {
    try {
      // Optimistic running state; the WS ticket_updated will carry the result.
      setEditing((prev) => ({ ...prev, review: { ...(prev.review || {}), status: 'running' } }));
      const { data } = await api.post(`/tickets/${ticket.id}/review`);
      if (data?.status && data.status !== 'running') {
        // no_branch / failed come back immediately — reflect it now.
        setEditing((prev) => ({ ...prev, review: { ...(prev.review || {}), status: data.status } }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('ticketDetail.reviewFailedToast'));
      setEditing((prev) => ({ ...prev, review: { ...(prev.review || {}), status: 'failed' } }));
    }
  };

  const confirmRemove = async () => {
    if (!onDeleteTicket) return;
    setDeleteLoading(true);
    try {
      await onDeleteTicket(ticket.id);
    } catch {
      /* toast from parent */
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!ticket) return null;
  const author = (id) => (id === AGENT_AUTHOR_ID
    ? t('agent.author')
    : users.find((u) => u.id === id)?.name || t('ticketDetail.unknownAuthor'));
  const authorEmail = (id) => users.find((u) => u.id === id)?.email || null;
  const review = editing.review;
  const projectRepos = project?.repos || [];
  const targetRepoIds = editing?.target_repo_ids?.length
    ? editing.target_repo_ids
    : projectRepos.map((r) => r.id);
  const targetRepos = projectRepos.filter((r) => targetRepoIds.includes(r.id));

  return (
    <>
      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('ticketDetail.deleteConfirmTitle')}
        description={t('ticketDetail.deleteConfirmDescription', { number: ticket.number })}
        confirmLabel={t('ticketDetail.deleteConfirmBtn')}
        cancelLabel={t('common.cancel')}
        variant="destructive"
        loading={deleteLoading}
        onConfirm={confirmRemove}
      />
      <ConfirmDialog
        open={!!commentDeleteTarget}
        onOpenChange={(open) => { if (!open) setCommentDeleteTarget(null); }}
        title={t('ticketDetail.commentDeleteConfirmTitle')}
        description={t('ticketDetail.commentDeleteConfirmDescription')}
        confirmLabel={t('ticketDetail.commentDeleteConfirmBtn')}
        cancelLabel={t('common.cancel')}
        variant="destructive"
        loading={commentDeleteLoading}
        onConfirm={confirmDeleteComment}
      />
      <div
        role="dialog"
        aria-modal="false"
        aria-label={`Ticket T${ticket.number}`}
        data-testid="ticket-detail"
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white sm:max-w-2xl border-l border-zinc-200 shadow-[-8px_0_40px_rgba(0,0,0,0.08)] pointer-events-auto"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 bg-white px-6 py-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] font-mono text-zinc-500">
            <span>{t('ticketDetail.label')}</span>
            <span className="text-zinc-900 font-semibold">T#{ticket.number}</span>
            <span className="text-zinc-300">·</span>
            <span>{ticket.id.slice(0, 8)}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="ticket-detail-copy-btn"
              onClick={copyContent}
              title={t('ticketDetail.copyContentHint')}
              aria-label={t('ticketDetail.copyContent')}
              className="inline-flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-[11px] font-mono text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            >
              <Copy size={14} weight="bold" />
              {t('ticketDetail.copyContent')}
            </button>
            <button
              type="button"
              data-testid="ticket-detail-close-btn"
              onClick={onClose}
              className="rounded-sm p-2 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
              aria-label={t('ticketDetail.closeAria')}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">
          <Input
            data-testid="ticket-detail-title"
            value={editing.title}
            onChange={(e) => setEditing({ ...editing, title: e.target.value })}
            onBlur={save}
            className="font-display text-xl font-black tracking-tight border-0 px-0 focus-visible:ring-0 shadow-none rounded-none"
          />

          <div className="grid grid-cols-3 gap-4 text-xs border-y border-zinc-200 py-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">{t('ticketDetail.statusLabel')}</div>
              <Select value={editing.status} onValueChange={(v) => { setEditing({ ...editing, status: v }); save({ status: v }); }}>
                <SelectTrigger data-testid="ticket-detail-status" className="h-8 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todo">{t('ticketDetail.statusBacklog')}</SelectItem>
                  <SelectItem value="in_progress">{t('ticketDetail.statusInProgress')}</SelectItem>
                  <SelectItem value="blocked">{t('ticketDetail.statusBlocked')}</SelectItem>
                  <SelectItem value="testing">{t('ticketDetail.statusTesting')}</SelectItem>
                  <SelectItem value="review">{t('ticketDetail.statusReview')}</SelectItem>
                  <SelectItem value="done">{t('ticketDetail.statusDone')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">{t('ticketDetail.priorityLabel')}</div>
              <Select value={editing.priority} onValueChange={(v) => { setEditing({ ...editing, priority: v }); save({ priority: v }); }}>
                <SelectTrigger data-testid="ticket-detail-priority" className="h-8 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t('priority.low')}</SelectItem>
                  <SelectItem value="medium">{t('priority.medium')}</SelectItem>
                  <SelectItem value="high">{t('priority.high')}</SelectItem>
                  <SelectItem value="urgent">{t('priority.urgent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">{t('ticketDetail.assigneeLabel')}</div>
              <Select value={editing.assignee_id || 'none'} onValueChange={(v) => { const assignee_id = v === 'none' ? null : v; setEditing({ ...editing, assignee_id }); save({ assignee_id }); }}>
                <SelectTrigger data-testid="ticket-detail-assignee" className="h-8 rounded-sm text-xs"><SelectValue placeholder={t('newTicket.unassigned')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('newTicket.unassigned')}</SelectItem>
                  {assignableUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <AgentPanel
            ticket={editing}
            project={project}
            steps={agentSteps}
            onTicket={(data) => { setEditing(data); onChange && onChange(); }}
          />

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{t('ticketDetail.descriptionLabel')}</div>
              <button
                type="button"
                data-testid="ticket-detail-attach-btn"
                onClick={handleAttachClick}
                disabled={uploadingAttachment}
                aria-label={t('newTicket.attachAria')}
                title={t('newTicket.attachBtn')}
                className="inline-flex items-center gap-1 text-[11px] font-mono text-zinc-500 hover:text-zinc-900 disabled:opacity-50"
              >
                <Paperclip size={12} weight="bold" />
                {uploadingAttachment ? '...' : t('newTicket.attachBtn')}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACH_ACCEPT}
                data-testid="ticket-detail-attach-input"
                onChange={handleAttachChange}
                className="hidden"
              />
            </div>
            {descEditMode ? (
              <div
                key="desc-edit"
                ref={descEditRef}
                contentEditable
                suppressContentEditableWarning
                data-testid="ticket-detail-description"
                onBlur={exitDescEdit}
                onPaste={handleDescriptionPaste}
                className="min-h-[10rem] w-full rounded-sm border border-zinc-200 bg-white px-3 py-2 text-[13px] font-mono shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-text"
              />
            ) : (
              <div
                key="desc-view"
                data-testid="ticket-detail-description-view"
                onClick={enterDescEdit}
                className="rounded-sm border border-zinc-200 bg-white px-3 py-2 min-h-[10rem] text-[13px] cursor-text hover:border-zinc-300"
                title={t('ticketDetail.descriptionEditHint')}
              >
                {editing.description ? (
                  <div className="prose prose-sm prose-zinc max-w-none prose-img:rounded-sm prose-img:max-h-96 prose-pre:bg-zinc-100 prose-pre:text-zinc-800 prose-code:text-zinc-800 prose-a:text-[#0055ff] break-words">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        img: ({ src, alt }) => (
                          <img
                            src={absolutizeMarkdownSrc(src)}
                            alt={alt || ''}
                            style={{ cursor: 'zoom-in' }}
                            onClick={(e) => { e.stopPropagation(); setLightboxSrc(absolutizeMarkdownSrc(src)); }}
                          />
                        ),
                        a: ({ href, children, ...rest }) => (
                          <a
                            href={href}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            {...rest}
                          >
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {editing.description}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <span className="text-zinc-400">{t('ticketDetail.descriptionPlaceholder')}</span>
                )}
              </div>
            )}
            {(editing.attachments || []).length > 0 && (
              <div className="mt-3" data-testid="ticket-detail-attachments">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1.5">
                  {t('newTicket.attachmentsLabel')}
                </div>
                <ul className="space-y-1">
                  {(editing.attachments || []).map((a) => {
                    const Icon = iconForMime(a.mime);
                    return (
                      <li
                        key={a.filename}
                        data-testid={`ticket-detail-attachment-${a.filename}`}
                        className="flex items-center gap-2 rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-xs group"
                      >
                        <Icon size={14} className="text-zinc-500 shrink-0" weight="bold" />
                        <a
                          href={absolutizeMarkdownSrc(a.url)}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono text-zinc-800 hover:text-[#0055ff] truncate flex-1"
                          title={a.original_name}
                        >
                          {a.original_name}
                        </a>
                        {a.bytes ? (
                          <span className="font-mono text-[10px] text-zinc-400 shrink-0">{formatBytes(a.bytes)}</span>
                        ) : null}
                        <button
                          type="button"
                          data-testid={`ticket-detail-attachment-remove-${a.filename}`}
                          onClick={() => removeAttachment(a.filename)}
                          aria-label={t('newTicket.attachRemoveAria')}
                          className="text-zinc-400 hover:text-red-600 shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                        >
                          <X size={12} weight="bold" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          <div data-testid="ticket-detail-labels">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1.5">
              {t('ticketDetail.labelsLabel')}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 rounded-sm border border-zinc-200 bg-white px-2 py-1.5 focus-within:border-zinc-400">
              {(editing.labels || []).map((l) => (
                <span
                  key={l}
                  data-testid={`ticket-detail-label-${l}`}
                  className={`inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 border rounded-sm ${labelColor(l)}`}
                >
                  {l}
                  <button
                    type="button"
                    data-testid={`ticket-detail-label-remove-${l}`}
                    onClick={() => removeLabel(l)}
                    aria-label={t('ticketDetail.removeLabelAria', { label: l })}
                    className="opacity-60 hover:opacity-100"
                  >
                    <X size={9} weight="bold" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                data-testid="ticket-detail-label-input"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                onKeyDown={onLabelKeyDown}
                onBlur={() => labelInput && addLabel(labelInput)}
                placeholder={
                  (editing.labels || []).length === 0
                    ? t('ticketDetail.addLabelPlaceholderEmpty')
                    : t('ticketDetail.addLabelPlaceholder')
                }
                className="flex-1 min-w-[120px] bg-transparent border-0 outline-none text-xs font-mono text-zinc-700 placeholder:text-zinc-400"
              />
            </div>
          </div>

          {/* Comments */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">
              {t('ticketDetail.commentsTitle', { count: comments.length })}
            </div>
            <div className="space-y-2 mb-3" data-testid="comments-list">
              {comments.map((c) => {
                const isOwn = currentUser?.id === c.author_id;
                const isEditing = editingCommentId === c.id;
                return (
                  <div
                    key={c.id}
                    data-testid={`comment-${c.id}`}
                    className="border border-zinc-200 rounded-sm p-3 bg-white group"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className="text-xs font-semibold">
                        {c.external_author ? c.external_author.name : author(c.author_id)}
                        {c.external_author && (
                          <span className="ml-1 font-normal text-zinc-500">
                            · {t('ticketDetail.viaSource', { source: (c.external_author.source || '').toUpperCase() || author(c.author_id) })}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-zinc-400 font-mono">{new Date(c.created_at).toLocaleString()}</div>
                      {c.updated_at && (
                        <div
                          className="text-[10px] text-zinc-400 font-mono"
                          title={new Date(c.updated_at).toLocaleString()}
                        >
                          ({t('ticketDetail.commentEditedTag')})
                        </div>
                      )}
                      {isOwn && !isEditing && (
                        <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                          <button
                            type="button"
                            data-testid={`comment-edit-btn-${c.id}`}
                            onClick={() => startEditComment(c)}
                            aria-label={t('ticketDetail.commentEditAria')}
                            className="rounded-sm p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                          >
                            <PencilSimple size={12} weight="bold" />
                          </button>
                          <button
                            type="button"
                            data-testid={`comment-delete-btn-${c.id}`}
                            onClick={() => setCommentDeleteTarget(c.id)}
                            aria-label={t('ticketDetail.commentDeleteAria')}
                            className="rounded-sm p-1 text-zinc-500 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash size={12} weight="bold" />
                          </button>
                        </div>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="space-y-2">
                        <Textarea
                          data-testid={`comment-edit-input-${c.id}`}
                          value={editingCommentBody}
                          onChange={(e) => setEditingCommentBody(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEditComment();
                            } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                              e.preventDefault();
                              saveEditComment();
                            }
                          }}
                          className="rounded-sm text-sm min-h-[72px]"
                          autoFocus
                        />
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            type="button"
                            data-testid={`comment-edit-cancel-${c.id}`}
                            onClick={cancelEditComment}
                            className="h-7 rounded-sm bg-transparent border border-zinc-200 text-zinc-700 hover:bg-zinc-100 text-xs"
                          >
                            {t('common.cancel')}
                          </Button>
                          <Button
                            type="button"
                            data-testid={`comment-edit-save-${c.id}`}
                            onClick={saveEditComment}
                            disabled={!editingCommentBody.trim() || editingCommentBody === c.body}
                            className="h-7 rounded-sm bg-black text-white hover:bg-zinc-800 text-xs"
                          >
                            {t('common.save')}
                          </Button>
                        </div>
                      </div>
                    ) : c.author_id === AGENT_AUTHOR_ID ? (
                      // The agent writes markdown: lists, MR links and test screenshots.
                      <div className="prose prose-sm prose-zinc max-w-none prose-img:rounded-sm prose-img:max-h-72 prose-a:text-[#0055ff] break-words">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            img: ({ src, alt }) => (
                              <img
                                src={absolutizeMarkdownSrc(src)}
                                alt={alt || ''}
                                style={{ cursor: 'zoom-in' }}
                                onClick={() => setLightboxSrc(absolutizeMarkdownSrc(src))}
                              />
                            ),
                            a: ({ href, children, ...rest }) => (
                              <a href={href} target="_blank" rel="noreferrer" {...rest}>{children}</a>
                            ),
                          }}
                        >
                          {c.body}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <div className="text-sm text-zinc-700 whitespace-pre-wrap">
                        {c.body.split(/(!\[[^\]]*\]\([^)]*\))/g).map((part, i) => {
                          const m = part.match(/^!\[[^\]]*\]\(([^)]*)\)$/);
                          if (!m) return part ? <span key={i}>{part}</span> : null;
                          const src = absolutizeMarkdownSrc(m[1]);
                          return (
                            <img
                              key={i}
                              src={src}
                              alt=""
                              onClick={() => setLightboxSrc(src)}
                              className="block max-h-72 max-w-full rounded-sm my-1 cursor-zoom-in"
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {commentImages.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="comment-pending-images">
                {commentImages.map((img, i) => (
                  <div key={img.url} className="relative group">
                    <img src={absolutizeMarkdownSrc(img.url)} alt="" className="h-16 w-auto rounded-sm border border-zinc-200" />
                    <button
                      type="button"
                      aria-label={t('newTicket.attachRemoveAria')}
                      onClick={() => setCommentImages((prev) => prev.filter((_, j) => j !== i))}
                      className="absolute -top-1.5 -right-1.5 rounded-full bg-white border border-zinc-300 p-0.5 text-zinc-500 hover:text-red-600"
                    >
                      <X size={10} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={addComment} className="flex items-end gap-2">
              <Textarea
                ref={commentInputRef}
                data-testid="comment-input"
                rows={1}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onPaste={handleCommentPaste}
                onKeyDown={(e) => {
                  // Enter sends, Shift+Enter breaks the line. Skip while an IME
                  // is composing so confirming a candidate doesn't send.
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    if (!uploadingCommentImage) e.currentTarget.form?.requestSubmit();
                  }
                }}
                placeholder={t('ticketDetail.commentPlaceholder')}
                className="rounded-sm min-h-9 max-h-48 resize-none py-[7px]"
              />
              <button
                type="button"
                data-testid="comment-attach-btn"
                onClick={() => commentFileRef.current?.click()}
                disabled={uploadingCommentImage}
                title={t('ticketDetail.commentAttachImage')}
                aria-label={t('ticketDetail.commentAttachImage')}
                className="shrink-0 h-9 inline-flex items-center justify-center rounded-sm border border-zinc-200 px-2.5 text-zinc-500 hover:text-zinc-900 hover:border-zinc-400 disabled:opacity-50"
              >
                {uploadingCommentImage ? <ArrowsClockwise size={14} className="animate-spin" /> : <Paperclip size={14} weight="bold" />}
              </button>
              <input
                ref={commentFileRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadCommentImage(f); }}
              />
              <Button data-testid="comment-submit-btn" type="submit" disabled={uploadingCommentImage} className="rounded-sm bg-black text-white hover:bg-zinc-800">{t('ticketDetail.commentSubmit')}</Button>
            </form>
          </div>

          {targetRepos.length > 0 && (
            <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50" data-testid="ticket-in-progress-branches">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">
                {t('ticketDetail.inProgressBranchesTitle')}
              </div>
              <div className="space-y-1">
                {targetRepos.map((repo) => {
                  const branch = (editing.in_progress_branches || {})[repo.id];
                  const isCreating = creatingBranchRepoId === repo.id;
                  return (
                    <div key={repo.id} className="flex items-center gap-2 text-xs">
                      <span className="font-mono font-semibold text-zinc-800 min-w-[100px] truncate">{repo.name || repo.id.slice(0, 8)}</span>
                      {branch ? (
                        <>
                          <code className="font-mono text-zinc-700 truncate flex-1">{branch}</code>
                          <button
                            type="button"
                            data-testid={`in-progress-copy-${repo.id}`}
                            onClick={() => copyCheckoutCmd(branch)}
                            title={t('ticketDetail.copyCheckout')}
                            className="text-zinc-500 hover:text-[#0055ff] shrink-0"
                          >
                            <Copy size={12} weight="bold" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-zinc-400 italic">{t('ticketDetail.noBranchYet')}</span>
                          <button
                            type="button"
                            data-testid={`create-branch-${repo.id}`}
                            onClick={() => createBranch(repo.id)}
                            disabled={isCreating || !!creatingBranchRepoId}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-black text-white hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed shrink-0 font-medium"
                          >
                            {isCreating ? (
                              <ArrowsClockwise size={12} weight="bold" className="animate-spin" />
                            ) : (
                              <GitBranch size={12} weight="bold" />
                            )}
                            {isCreating ? t('ticketDetail.branchCreating') : t('ticketDetail.createBranch')}
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {targetRepos.length > 0 && (
            <div className="border border-zinc-200 rounded-md p-3" data-testid="ticket-review-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">
                  {t('ticketDetail.reviewTitle')}
                </div>
                <button
                  type="button"
                  data-testid="run-review-btn"
                  onClick={runReview}
                  disabled={review?.status === 'running'}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-[#0055ff] text-white text-xs font-medium hover:bg-[#0044cc] disabled:opacity-50"
                >
                  {review?.status === 'running' ? (
                    <ArrowsClockwise size={12} weight="bold" className="animate-spin" />
                  ) : (
                    <MagnifyingGlass size={12} weight="bold" />
                  )}
                  {review?.status === 'running'
                    ? t('ticketDetail.reviewRunning')
                    : review && review.status !== 'idle'
                    ? t('ticketDetail.reviewRerun')
                    : t('ticketDetail.reviewRun')}
                </button>
              </div>

              {!review || review.status === 'idle' ? (
                <p className="text-[11px] text-zinc-400">{t('ticketDetail.reviewHint')}</p>
              ) : review.status === 'running' ? (
                <p className="text-xs text-zinc-500">{t('ticketDetail.reviewRunningBody')}</p>
              ) : review.status === 'no_branch' ? (
                <p className="text-xs text-amber-700">{review.error || t('ticketDetail.reviewNoBranch')}</p>
              ) : review.status === 'failed' ? (
                <p className="text-xs text-red-600">{review.error || t('ticketDetail.reviewFailedToast')}</p>
              ) : (
                <div className="space-y-3">
                  {(review.repos || []).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {review.repos.map((r) => (
                        <span key={r.repo_id} className="inline-flex items-center gap-1.5 text-[10px] font-mono px-2 py-1 rounded-sm border border-zinc-200 bg-zinc-50">
                          <span className="font-semibold text-zinc-700">{r.repo_name}</span>
                          {r.error ? (
                            <span className="text-red-500 normal-case">{r.error}</span>
                          ) : (
                            <span className="text-zinc-500">
                              {r.files_changed} · <span className="text-emerald-600">+{r.additions}</span>/<span className="text-red-500">-{r.deletions}</span>
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                  {review.summary && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">{t('ticketDetail.reviewWhatChanged')}</div>
                      <div className="text-[13px] text-zinc-800 prose prose-sm max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{review.summary}</ReactMarkdown></div>
                    </div>
                  )}
                  {review.risks && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">{t('ticketDetail.reviewRisks')}</div>
                      <div className="text-[13px] text-zinc-800 prose prose-sm max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{review.risks}</ReactMarkdown></div>
                    </div>
                  )}
                  {(review.repos || []).filter((r) => r.summary || r.risks).length > 1 && (
                    <details>
                      <summary className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 cursor-pointer select-none">
                        {t('ticketDetail.reviewPerRepo')}
                      </summary>
                      <div className="mt-2 space-y-2">
                        {review.repos.filter((r) => r.summary || r.risks).map((r) => (
                          <div key={r.repo_id} className="border-l-2 border-zinc-200 pl-2">
                            <div className="text-[11px] font-mono font-semibold text-zinc-700 mb-0.5">{r.repo_name}</div>
                            {r.summary && (
                              <div className="text-[12px] text-zinc-700 prose prose-sm max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{r.summary}</ReactMarkdown></div>
                            )}
                            {r.risks && (
                              <div className="mt-1">
                                <div className="text-[9px] uppercase tracking-wider font-semibold text-zinc-400">{t('ticketDetail.reviewRisks')}</div>
                                <div className="text-[12px] text-zinc-700 prose prose-sm max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{r.risks}</ReactMarkdown></div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                  {review.generated_at && (
                    <div className="text-[10px] text-zinc-400">
                      {t('ticketDetail.reviewGeneratedAt', { date: new Date(review.generated_at).toLocaleString() })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-zinc-200 bg-white px-6 py-3">
          <div
            data-testid="ticket-detail-meta"
            className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-zinc-500"
          >
            <span data-testid="ticket-detail-author" className="truncate">
              {t('ticketDetail.createdBy', { name: author(editing.creator_id) })}
            </span>
            {authorEmail(editing.creator_id) && (
              <>
                <span className="text-zinc-300">·</span>
                <span data-testid="ticket-detail-author-email" className="truncate font-mono">
                  {authorEmail(editing.creator_id)}
                </span>
              </>
            )}
            {editing.created_at && (
              <>
                <span className="text-zinc-300">·</span>
                <time
                  data-testid="ticket-detail-created-at"
                  dateTime={editing.created_at}
                  title={new Date(editing.created_at).toLocaleString()}
                  className="font-mono"
                >
                  {new Date(editing.created_at).toLocaleString()}
                </time>
              </>
            )}
          </div>
          <Button
            data-testid="ticket-delete-btn"
            type="button"
            variant="outline"
            onClick={() => setDeleteConfirmOpen(true)}
            className="shrink-0 rounded-sm border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            {t('ticketDetail.deleteAria')}
          </Button>
        </div>
      </div>

      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 cursor-zoom-out"
          onClick={() => setLightboxSrc(null)}
        >
          <img
            src={lightboxSrc}
            alt=""
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-sm shadow-2xl cursor-default"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
